/**
 * PaymentsDataTable — v2 Payments table.
 *
 * Three buckets keyed off voucher.status; all labels, URL slugs, and action
 * gating come from `Payments.constants.ts`. Do not hard-code status strings
 * here — update the constants module instead.
 */

// react
import React, { useMemo } from 'react';

// i18n
import { useTranslation } from 'react-i18next';

// mui
import { alpha, Avatar, Box, Button, Stack, Tooltip, Typography } from '@mui/material';

// icons
import {
	AccountTree as IconActivity,
	Business as IconSupplier,
	CalendarMonth as IconCalendar,
	Description as IconContract,
	ReportProblem as IconException,
	Visibility as IconView,
} from '@mui/icons-material';

// components
import {
	ColumnDef,
	DataTable,
	FilterConfig,
	RowAction,
	SortDirection,
	TabConfig,
} from '../../../ui/DataTable/DataTable';
import { TruncatedText } from '../../../ui/TruncatedText/TruncatedText';
import { VoucherStatusChip } from '../../../ui/VoucherStatusChip/VoucherStatusChip';

// data
import { COLORS, GRADIENTS, VoucherDetails } from '../../../../data';

// utils
import { formatCurrency } from '../../../../utils/currency';
import { restateViaCapturedRates } from '../../../../utils/localCurrency';

// constants
import { PAYMENT_BUCKETS, PAYMENT_BUCKET_ORDER, PaymentBucket } from './Payments.constants';

// Tab Keys
//
// The old `PAYMENT_TABS` export and `PaymentTabValue` alias have been removed
// in favor of `PaymentBucket` from `Payments.constants.ts`. Consumers should
// import directly from there.

// Props

interface PaymentsDataTableProps {
	/** Voucher data for the current bucket */
	data: VoucherDetails[];
	/** Whether data is loading */
	isLoading?: boolean;
	/** Current search value */
	searchValue?: string;
	/** Search change handler */
	onSearchChange?: (value: string) => void;
	/** Current sort column */
	orderBy?: string;
	/** Current sort direction */
	order?: SortDirection;
	/** Sort change handler */
	onSortChange?: (orderBy: string, order: SortDirection) => void;
	/** Current page (1-indexed) */
	page?: number;
	/** Rows per page */
	rowsPerPage?: number;
	/** Total count for pagination */
	totalCount?: number;
	/** Page change handler */
	onPageChange?: (page: number) => void;
	/** Rows per page change handler */
	onRowsPerPageChange?: (rowsPerPage: number) => void;
	/** Row click handler (navigate to voucher detail) */
	onRowClick?: (voucher: VoucherDetails) => void;
	/** Redeem handler (Awaiting Review bucket) */
	onRedeem?: (voucher: VoucherDetails) => void;
	/** Mark-complete handler (Awaiting Payment bucket; vouchers paid off-hub) */
	onComplete?: (voucher: VoucherDetails) => void;

	// === Filters ===
	/** Filter options for the Contract dropdown */
	contractOptions?: { value: string | number; label: string }[];
	/** Current filter values */
	filterValues?: Record<string, string | number>;
	/** Filter change handler */
	onFilterChange?: (key: string, value: string | number) => void;

	// === Tabs ===
	/** Active bucket */
	activeBucket: PaymentBucket;
	/** Bucket change handler */
	onBucketChange: (bucket: PaymentBucket) => void;
	/** Counts for each bucket badge (project-wide) */
	bucketCounts: Record<PaymentBucket, number> | null;
	/** Project reporting currency, used to restate amounts via captured FX. */
	reportingCurrency?: string | null;
	/** Project local currency, used to restate amounts via captured FX. */
	localCurrency?: string | null;
	/**
	 * Per-contract local-per-reporting hint, keyed by contract id. When a row's
	 * contract is in the map, the hint is passed to `restateViaCapturedRates`
	 * as the ground-truth rate — this neutralises pre-PR-3862 vouchers whose
	 * captured rate rows stored the inverted (raw user entry) direction.
	 */
	contractBudgetRates?: Record<number, number>;
}

// Helpers

/** Color scheme for an Amount cell, derived from the row's bucket. */
const getAmountColor = (status: VoucherDetails['status']) => {
	switch (status) {
		case 'REDEEMED':
			return COLORS.success;
		case 'READY':
		case 'VERIFIED':
			return COLORS.info;
		default:
			return { dark: '#4b5563', light: '#f3f4f6', main: '#6b7280' };
	}
};

// Component

export const PaymentsDataTable = ({
	data,
	isLoading = false,
	searchValue = '',
	onSearchChange,
	orderBy,
	order = 'DESC',
	onSortChange,
	page = 1,
	rowsPerPage = 10,
	totalCount,
	onPageChange,
	onRowsPerPageChange,
	onRowClick,
	onRedeem,
	onComplete,
	contractOptions = [],
	filterValues = {},
	onFilterChange,
	activeBucket,
	onBucketChange,
	bucketCounts,
	reportingCurrency = null,
	localCurrency = null,
	contractBudgetRates = {},
}: PaymentsDataTableProps) => {
	const { t } = useTranslation();

	const showRedeemAction = activeBucket === 'AWAITING_REVIEW';
	const showCompleteAction = activeBucket === 'AWAITING_PAYMENT';

	// =========================================================================
	// Filters
	// =========================================================================

	const filters: FilterConfig[] = useMemo(
		() => [
			{
				key: 'contract',
				label: t('contract'),
				options: [{ value: 'ALL', label: t('spendPlan.payments.allContracts') }, ...contractOptions],
			},
			{
				key: 'channel',
				label: t('spendPlan.payments.paymentChannel'),
				options: [
					{ value: 'ALL', label: t('spendPlan.payments.allChannels') },
					{ value: 'BANK', label: 'Bank Transfer' },
					{ value: 'CARD', label: 'Card' },
					{ value: 'MOBILE_MONEY', label: 'Mobile Money' },
					{ value: 'STABLECOIN', label: 'Stablecoin' },
					{ value: 'OTHER', label: 'Other' },
				],
			},
		],
		[contractOptions]
	);

	// =========================================================================
	// Tabs
	// =========================================================================

	const tabs: TabConfig[] = useMemo(
		() =>
			PAYMENT_BUCKET_ORDER.map((bucket) => {
				const def = PAYMENT_BUCKETS[bucket];
				return {
					label: t(def.i18nKey, def.fallback),
					value: bucket,
					count: bucketCounts?.[bucket] ?? 0,
					color: def.color,
				};
			}),
		[t, bucketCounts]
	);

	// =========================================================================
	// Columns
	// =========================================================================

	const columns: ColumnDef<VoucherDetails>[] = useMemo(
		() => [
			{
				id: 'contractNum',
				label: t('contract'),
				minWidth: 120,
				sortable: true,
				render: (row) => (
					<Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
						<Avatar
							sx={{
								bgcolor: alpha(GRADIENTS.contract.icon, 0.12),
								color: GRADIENTS.contract.icon,
								height: 28,
								width: 28,
								flexShrink: 0,
							}}>
							<IconContract sx={{ fontSize: 14 }} />
						</Avatar>
						<TruncatedText
							variant="body2"
							text={row.contractNum}
							sx={{
								color: 'text.primary',
								fontFamily: 'monospace',
								fontWeight: 600,
							}}
						/>
					</Stack>
				),
			},
			{
				id: 'supplierName',
				label: t('recipient', 'Recipient'),
				minWidth: 140,
				sortable: true,
				render: (row) => (
					<Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
						<IconSupplier sx={{ fontSize: 14, color: 'text.secondary', flexShrink: 0 }} />
						<TruncatedText variant="body2" color="text.secondary" text={row.supplierName} />
					</Stack>
				),
			},
			{
				id: 'activityName',
				label: t('activity'),
				minWidth: 140,
				sortable: true,
				render: (row) => (
					<Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
						<IconActivity sx={{ fontSize: 14, color: 'text.secondary', flexShrink: 0 }} />
						<TruncatedText variant="body2" color="text.secondary" text={row.activityName} />
					</Stack>
				),
			},
			{
				id: 'name',
				label: t('voucher'),
				minWidth: 160,
				sortable: true,
				sortKey: 'conditionName',
				render: (row) => {
					const hasException = !!row.exception;
					const hasChecklistException =
						row.sectionResponses?.some((sr) => sr.hasException) ?? false;
					const showExceptionIndicator = hasException || hasChecklistException;

					return (
						<Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
							<TruncatedText variant="body2" text={row.name} sx={{ fontWeight: 600 }} />
							{showExceptionIndicator && (
								<Tooltip
									title={
										hasChecklistException
											? t('spendPlan.voucherHasException', 'Has unmet verification questions')
											: t('spendPlan.conditionOverridden', 'Condition was overridden')
									}>
									<IconException sx={{ fontSize: 16, color: COLORS.error.main, flexShrink: 0 }} />
								</Tooltip>
							)}
						</Stack>
					);
				},
			},
			{
				id: 'docs',
				label: t('docs'),
				minWidth: 60,
				align: 'center' as const,
				sortable: false,
				render: () => (
					<Box sx={{ display: 'flex', justifyContent: 'center' }}>
						<Typography variant="body2" color="text.disabled">
							—
						</Typography>
					</Box>
				),
			},
			{
				id: 'amount',
				label: t('amount'),
				minWidth: 120,
				align: 'right' as const,
				sortable: true,
				sortKey: 'amount',
				render: (row) => {
					const amountColor = getAmountColor(row.status);
					// Restate the payment into the reporting and local currencies
					// using the FX rates captured on the voucher at payment time.
					// The per-contract budget rate is passed as a hint so legacy
					// vouchers with inverted captured rates still restate sanely.
					const hint = row.contractId != null ? contractBudgetRates[row.contractId] : undefined;
					const { reporting, local } = restateViaCapturedRates({
						amount: row.paymentAmount,
						currency: row.currency,
						reportingCurrency,
						localCurrency,
						rates: row.exchangeRates,
						localPerReportingHint: hint ?? null,
					});
					const equivalents: { currency: string; value: number }[] = [];
					if (reporting != null && reportingCurrency && row.currency !== reportingCurrency) {
						equivalents.push({ currency: reportingCurrency, value: reporting });
					}
					if (
						local != null &&
						localCurrency &&
						row.currency !== localCurrency &&
						localCurrency !== reportingCurrency
					) {
						equivalents.push({ currency: localCurrency, value: local });
					}
					return (
						<Box>
							<Typography variant="body2" sx={{ fontWeight: 700, color: amountColor.main }}>
								{formatCurrency({ value: row.paymentAmount, currency: row.currency })}
							</Typography>
							{equivalents.map((eq) => (
								<Typography
									key={eq.currency}
									variant="body2"
									sx={{ fontWeight: 500, color: '#6b7280' }}>
									{formatCurrency({ value: eq.value, currency: eq.currency })}
								</Typography>
							))}
							<Typography variant="body2" color="text.disabled">
								{row.paymentPercentage}%
							</Typography>
						</Box>
					);
				},
			},
			{
				id: 'status',
				label: t('status'),
				minWidth: 120,
				sortable: true,
				render: (row) => {
					// Awaiting Review bucket: vouchers waiting to be redeemed.
					if (showRedeemAction && (row.status === 'READY' || row.status === 'VERIFIED')) {
						return (
							<Button
								variant="contained"
								color="success"
								size="small"
								onClick={(e) => {
									e.stopPropagation();
									onRedeem?.(row);
								}}
								sx={{
									borderRadius: 2,
									px: 2,
									py: 0.5,
									textTransform: 'none',
									fontWeight: 600,
									fontSize: '0.75rem',
								}}>
								{t('redeem')}
							</Button>
						);
					}

					// Awaiting Payment bucket: vouchers approved + waiting to be marked paid off-hub.
					if (showCompleteAction && row.status === 'READY') {
						return (
							<Button
								variant="contained"
								color="success"
								size="small"
								onClick={(e) => {
									e.stopPropagation();
									onComplete?.(row);
								}}
								sx={{
									borderRadius: 2,
									px: 2,
									py: 0.5,
									textTransform: 'none',
									fontWeight: 600,
									fontSize: '0.75rem',
								}}>
								{t('spendPlan.voucher.markPaid', 'Manually Enter Payment Info')}
							</Button>
						);
					}

					// Paid bucket: show the verified-at date alongside the chip.
					if (row.status === 'REDEEMED' && row.verifiedAt) {
						return (
							<Stack direction="row" alignItems="center" spacing={0.5}>
								<IconCalendar sx={{ fontSize: 14, color: 'text.disabled' }} />
								<Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
									{row.verifiedAt}
								</Typography>
							</Stack>
						);
					}

					return <VoucherStatusChip status={row.status} />;
				},
			},
		],
		[
			t,
			showRedeemAction,
			onRedeem,
			showCompleteAction,
			onComplete,
			reportingCurrency,
			localCurrency,
			contractBudgetRates,
		]
	);

	// =========================================================================
	// Row Actions
	// =========================================================================

	const rowActions: RowAction<VoucherDetails>[] = useMemo(
		() => [
			{
				label: 'View Details',
				icon: <IconView sx={{ fontSize: 18, color: 'text.secondary' }} />,
				onClick: (row) => onRowClick?.(row),
			},
		],
		[onRowClick]
	);

	// =========================================================================
	// Render
	// =========================================================================

	return (
		<DataTable<VoucherDetails>
			data={data}
			columns={columns}
			getRowKey={(row) => row.id}
			loading={isLoading}
			emptyMessage={
				activeBucket === 'AWAITING_REVIEW'
					? t('spendPlan.empty.payments.readyForPayment.title')
					: activeBucket === 'AWAITING_PAYMENT'
						? t('spendPlan.empty.payments.pending.title')
						: t('spendPlan.empty.payments.completed.title')
			}
			emptyContent={
				activeBucket === 'AWAITING_REVIEW'
					? t('spendPlan.empty.payments.readyForPayment.content')
					: activeBucket === 'AWAITING_PAYMENT'
						? t('spendPlan.empty.payments.pending.content')
						: undefined
			}
			// Tabs
			tabs={tabs}
			activeTab={activeBucket}
			onTabChange={(value) => onBucketChange(value as PaymentBucket)}
			// Search
			searchPlaceholder={t('spendPlan.payments.searchPlaceholder')}
			searchValue={searchValue}
			onSearchChange={onSearchChange}
			showFilters
			filters={filters}
			filterValues={filterValues}
			onFilterChange={onFilterChange}
			// Sorting
			orderBy={orderBy}
			order={order}
			onSortChange={onSortChange}
			// Pagination
			page={page}
			rowsPerPage={rowsPerPage}
			totalCount={totalCount}
			onPageChange={onPageChange}
			onRowsPerPageChange={onRowsPerPageChange}
			// Row interaction
			rowActions={rowActions}
			onRowClick={onRowClick}
		/>
	);
};
